import { PricingCardLayout } from '@upheal/shared/components/shared/Billing/PricingCard/PricingCardLayout';
import {
    getDescriptionFirstParagraphPerTier,
    getDescriptionItemsPerTier,
} from './PricingCard.data';
import {
    isValidTier,
    PricingTier,
    POPULAR_TIER,
    getPlanDisplayName,
} from '@upheal/billing/utils/plans';
import { PricingCardPrice } from '@upheal/shared/components/shared/Billing/PricingCard/PricingCardPrice';
import { PricingCardCTA } from '@upheal/shared/components/shared/Billing/PricingCard/PricingCardCTA';
import { PricingCardDescriptionDetails } from '@upheal/shared/components/shared/Billing/PricingCard/PricingCardDescriptionDetails';
import { PricingCardTitle } from '@upheal/shared/components/shared/Billing/PricingCard/PricingCardTitle';
import { useEffect, useMemo } from 'react';
import { captureException } from '../../../../utils/sentry';
import { Skeleton } from '@mui/material';
import { usePlansPricing } from '@upheal/billing/hooks/usePlansPricing';

type Props = {
    tier: PricingTier;
    yearlyPricing?: boolean;
    referralPricing?: boolean;
    showDiscountInfo?: boolean;
    forceHideEmptyYearlySavingsArea?: boolean;
    ctaPlacement?: 'middle' | 'bottom' | 'none';
    isDimmed?: boolean;
    isCurrentPlan?: boolean;
    flexGrow?: number;
    uiLocation?: string;
    onClick?: () => void | Promise<void>;
};

export const PricingCard = (props: Props) => {
    const {
        tier,
        yearlyPricing,
        referralPricing,
        showDiscountInfo = true,
        forceHideEmptyYearlySavingsArea,
        ctaPlacement = 'middle',
        isDimmed,
        isCurrentPlan,
        flexGrow,
        uiLocation,
        onClick,
    } = props;
    const tierName = getPlanDisplayName(tier);
    const isPopular = POPULAR_TIER === tier;

    const { pricingData } = usePlansPricing();

    useEffect(() => {
        if (!isValidTier(tier)) {
            captureException('Invalid pricing tier used in PricingCard', {
                tier,
                uiLocation,
            });
        }
    }, [tier, uiLocation]);

    const planPricing = useMemo(() => {
        if (tier === 'free') {
            return {
                monthlyPrice: 0,
                monthlyPriceWithYearlyDiscount: 0,
                monthlyPriceWithYearlyAndReferralDiscount: 0,
            };
        }
        const tierPricing = pricingData?.timeBasedPlansPricing?.find(
            (pricing) => pricing.planId === tier,
        );
        if (!tierPricing) {
            return null;
        }
        return {
            monthlyPrice: tierPricing.monthlyPrice,
            monthlyPriceWithYearlyDiscount:
                tierPricing.monthlyPriceYearlyDiscount,
            monthlyPriceWithYearlyAndReferralDiscount:
                tierPricing.monthlyPriceYearlyReferralDiscount,
        };
    }, [pricingData, tier]);

    if (!isValidTier(tier)) {
        return null;
    }

    return (
        <PricingCardLayout
            title={
                <PricingCardTitle
                    tier={tier}
                    isPopular={!isDimmed && isPopular}
                    isDimmed={isDimmed}
                >
                    {tierName}
                </PricingCardTitle>
            }
            descriptionDetails={
                <PricingCardDescriptionDetails
                    paragraph={getDescriptionFirstParagraphPerTier()[tier]}
                    items={getDescriptionItemsPerTier()[tier]}
                />
            }
            price={
                !planPricing ? (
                    <Skeleton height={61} />
                ) : (
                    <PricingCardPrice
                        price={planPricing.monthlyPrice}
                        currency={pricingData?.currency}
                        priceWithYearlyDiscount={
                            referralPricing
                                ? planPricing.monthlyPriceWithYearlyAndReferralDiscount
                                : planPricing.monthlyPriceWithYearlyDiscount
                        }
                        yearlyPricing={yearlyPricing}
                        referralPricing={referralPricing}
                        showDiscountInfo={showDiscountInfo}
                        forceHideEmptyYearlySavingsArea={
                            forceHideEmptyYearlySavingsArea
                        }
                    />
                )
            }
            cta={
                ctaPlacement !== 'none' && (
                    <PricingCardCTA
                        title={`Go ${tierName}`}
                        onClick={onClick}
                        uiLocation={uiLocation}
                        uiProperty={tier}
                        {...(isCurrentPlan && {
                            title: 'Your current plan',
                            disabled: true,
                            color: 'dark',
                        })}
                    />
                )
            }
            ctaPlacement={ctaPlacement}
            isDimmed={isDimmed}
            isHighlighted={!isDimmed && isPopular}
            flexGrow={flexGrow}
        />
    );
};
